/**
 * Client-side PDF -> Markdown converter using pdfjs-dist positional + font metadata.
 * No network calls, no server processing — PDF content stays in the browser.
 *
 * Detects:
 *   - Headings (H1/H2/H3) based on font size ratio vs the document's body font
 *   - Bold / italic runs from font-name matching (Bold/Black/Heavy, Italic/Oblique)
 *   - Bulleted lists from bullet glyphs (•, ●, ▪, ◦, ·, ・) at line start
 *   - Paragraph breaks from vertical gaps > 1.8x body font size
 *   - Page-number-only lines and "-- N of M --" markers are dropped
 */
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";

const BULLET_GLYPHS = /^[\u2022\u25CF\u25AA\u25E6\u00B7\u30FB]\s*/;
const LONE_BULLET = /^[\u2022\u25CF\u25AA\u25E6\u00B7\u30FB\-\u2013\u2014\*]$/;
const PAGE_FOOTER = /^(?:-{2,}\s*)?(?:page\s+)?\d+(?:\s+of\s+\d+)?(?:\s*-{2,})?$/i;

function classifyFont(fontName) {
  const n = (fontName || "").toLowerCase();
  return {
    bold: /bold|black|heavy|semibold/.test(n),
    italic: /italic|oblique/.test(n)
  };
}

function median(arr) {
  if (!arr.length) return 0;
  const s = [...arr].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

export async function pdfToMarkdown(arrayBuffer) {
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const lines = []; // { text, y, page, size, isBold, isItalic }

  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p);
    // Populate commonObjs so page.commonObjs.get(fontName) resolves the real font name
    try { await page.getOperatorList(); } catch { /* non-fatal */ }
    const content = await page.getTextContent();

    // Bucket items into visual lines by rounded Y position (2px tolerance)
    const buckets = new Map();
    for (const it of content.items) {
      if (typeof it.str !== "string" || !it.str.trim()) continue;
      const size = it.height || Math.abs(it.transform?.[3]) || 12;
      const x = it.transform?.[4] || 0;
      const y = it.transform?.[5] || 0;
      let fontName = it.fontName || "";
      try {
        const f = page.commonObjs.get(it.fontName);
        if (f?.name) fontName = f.name;
      } catch { /* internal id is fine as fallback */ }
      const { bold, italic } = classifyFont(fontName);
      const key = Math.round(y / 4) * 4;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push({ str: it.str, x, size, bold, italic });
    }

    const ys = [...buckets.keys()].sort((a, b) => b - a); // top-to-bottom (PDF y descends)
    for (const y of ys) {
      const items = buckets.get(y).sort((a, b) => a.x - b.x);
      const text = items.map(i => i.str).join(" ").replace(/\s+/g, " ").trim();
      if (!text) continue;
      if (PAGE_FOOTER.test(text)) continue; // drop page-number-only lines
      const size = Math.max(...items.map(i => i.size));
      const isBold = items.every(i => i.bold);
      const isItalic = !isBold && items.every(i => i.italic);
      lines.push({ text, y, page: p, size, isBold, isItalic });
    }
  }

  if (!lines.length) return "";

  // Merge orphan-bullet lines (a line containing ONLY a bullet char) with the
  // closest adjacent line — reportlab and many PDF generators emit the bullet
  // glyph on a slightly different baseline than its list-item text.
  const merged = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (LONE_BULLET.test(line.text)) {
      const nextSamePage = lines[i + 1] && lines[i + 1].page === line.page ? lines[i + 1] : null;
      const prevSamePage = merged[merged.length - 1] && merged[merged.length - 1].page === line.page ? merged[merged.length - 1] : null;
      const nextDist = nextSamePage ? Math.abs(nextSamePage.y - line.y) : Infinity;
      const prevDist = prevSamePage ? Math.abs(prevSamePage.y - line.y) : Infinity;
      if (nextDist <= prevDist && nextSamePage) {
        nextSamePage._isBullet = true;
      } else if (prevSamePage) {
        prevSamePage._isBullet = true;
      }
      continue; // drop the orphan bullet itself
    }
    merged.push(line);
  }

  // Determine the document's body font size (median across all detected lines)
  const bodySize = median(merged.map(l => l.size)) || 12;

  const out = [];
  let prevY = null;
  let prevPage = null;
  for (const line of merged) {
    // Blank line for cross-page or big-gap paragraph breaks
    if (prevPage !== null && line.page !== prevPage) out.push("");
    else if (prevY !== null && prevY - line.y > bodySize * 1.8) out.push("");

    let text = line.text;

    // Bullet from orphan-merge (see above) OR from a leading glyph within the same line
    let prefix = "";
    if (line._isBullet) {
      prefix = "- ";
    } else if (BULLET_GLYPHS.test(text)) {
      text = text.replace(BULLET_GLYPHS, "");
      prefix = "- ";
    }

    // Heading detection (size-driven) — only if no bullet prefix already
    if (!prefix) {
      if (line.size >= bodySize * 1.6) prefix = "# ";
      else if (line.size >= bodySize * 1.3) prefix = "## ";
      else if (line.size >= bodySize * 1.12 && line.isBold) prefix = "### ";
    }

    // Bold / italic emphasis on non-heading, non-bullet lines
    if (!prefix && line.isBold) text = `**${text}**`;
    else if (!prefix && line.isItalic) text = `*${text}*`;

    out.push(prefix + text);
    prevY = line.y;
    prevPage = line.page;
  }

  return out.join("\n").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}
